import React, { useState, useContext } from "react";
import CartContext from "../../Context/CartContext";
import { db } from "../../utils/config";
import { addDoc, collection, doc, getDoc, updateDoc } from "firebase/firestore";
import { Input, Button, VStack, Heading, Text } from "@chakra-ui/react";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../../utils/config";

// Define el componente de Checkout
export const Checkout = () => {
  // Utiliza el contexto del carrito para acceder a la información del carrito
  const { cart, getTotal, clear } = useContext(CartContext);

  // Establece el estado local para los datos del usuario, coincidencia de correos electrónicos y errores de formulario
  const [user, setUser] = useState({
    name: "",
    phone: "",
    email: "",
    repeatedEmail: "",
  });
  const [emailMatch, setEmailMatch] = useState(true);
  const [formErrors, setFormErrors] = useState({}); // Estado para almacenar errores de validación
  const navigate = useNavigate();
  const [currentUser] = useAuthState(auth);

  const updateUser = (event) => {
    // event representa el evento que desencadenó la llamada a la función, como un cambio en un campo de entrada.

    setUser((user) => ({
      // setUser es una función proporcionada por el hook useState para actualizar el estado local llamado 'user'.
      ...user, // Se utiliza el operador de propagación para copiar todas las propiedades existentes del objeto 'user'.
      [event.target.name]: event.target.value, // Se actualiza la propiedad correspondiente según el campo de entrada que ha cambiado.

      // Se utiliza una función para asegurar que la actualización del estado sea basada en el estado anterior ('user'). Se crea un nuevo objeto que copia todas las propiedades existentes de 'user' (...user) y luego actualiza la propiedad correspondiente utilizando el nombre del campo (event.target.name) y el valor del campo de entrada (event.target.value).
    }));
  };

  // Función para validar si los campos de correo electrónico coinciden
  const validateEmails = () => {
    if (user.email === user.repeatedEmail) {
      setEmailMatch(true);
    } else {
      setEmailMatch(false);
    }
  };

  // Función para validar el formulario y manejar el procesamiento del pedido
  const validateForm = () => {
    const errors = {};

    // Verifica si los campos requeridos están vacíos y establece mensajes de error en consecuencia
    if (!user.name) {
      errors.name = "Campo requerido";
    }

    if (!user.phone) {
      errors.phone = "Campo requerido";
    }

    if (!user.email) {
      errors.email = "Campo requerido";
    }

    if (!user.repeatedEmail) {
      errors.repeatedEmail = "Campo requerido";
    }

    // Establece los errores en el estado y devuelve un indicador de validez del formulario
    setFormErrors(errors);
    //Object.keys(errors).length === 0 verifica si el objeto errors no tiene ninguna propiedad, lo cual significa que no hay mensajes de error.
    return Object.keys(errors).length === 0;
  };

  // Define la función asincrónica getOrder, que se encargará de procesar la orden de compra
  const getOrder = async () => {
    // Valida el formulario y los correos electrónicos antes de proceder
    const isFormValid = validateForm();
    validateEmails();

    // Si el formulario es válido y los correos electrónicos coinciden
    if (isFormValid && emailMatch) {
      // Obtiene una referencia a la colección "orders" en Firebase Firestore
      const ordersCollection = collection(db, "orders");

      try {
        // Recorre cada elemento en el carrito
        for (const item of cart) {
          // Obtiene una referencia al documento del producto en Firebase Firestore
          const productRef = doc(db, "products", item.id);
          const productDoc = await getDoc(productRef);

          // Obtiene el stock actual del producto
          const currentStock = productDoc.data().stock;

          // Verifica si hay suficiente stock para el elemento en el carrito
          if (currentStock >= item.quantity) {
            // Actualiza el stock del producto restando la cantidad comprada
            await updateDoc(productRef, {
              stock: currentStock - item.quantity,
            });
          } else {
            console.error(`No hay suficiente stock para ${item.name}`);
            // Maneja la situación de stock insuficiente (puedes mostrar una alerta, etc.)
          }
        }

        // Crea un objeto "order" con la información del comprador, los elementos del carrito y el total
        const order = {
          buyer: user,
          items: cart,
          total: getTotal(),
        };

        // Agrega un nuevo documento (orden) a la colección "orders"
        const orderDocRef = await addDoc(ordersCollection, order);
        const orderId = orderDocRef.id;

        // Muestra una ventana emergente de SweetAlert2 para informar al usuario sobre la compra exitosa
        Swal.fire({
          title: `Muchas gracias por tu compra, ${user.name}`,
          text: `Te hemos enviado un mail a ${user.email} con tu orden de compra ID: ${orderId}`,
          icon: "success",
          showCancelButton: false,
          customClass: {
            confirmButton: "alertButton",
          },
        }).then(function () {
          // Después de que el usuario confirma la alerta, realiza las siguientes acciones:
          // Limpia el carrito de compras
          clear();
          // Redirige al usuario a la página principal
          navigate("/");
        });
      } catch (error) {
        console.error("Error al procesar la orden:", error);
        // Maneja el error (puedes mostrar una alerta, etc.)
      }
    } else {
      // Muestra un mensaje de error si el formulario no es válido
      Swal.fire({
        title: "Error",
        text: "Por favor, verifica tus datos.",
        icon: "error",
      });
    }
  };

  return (
    <VStack
      justify="center"
      align="center"
      maxW="7xl"
      mx="auto"
      p={6}
      bg="background-checkout"
    >
      <Heading
        as="h1"
        fontSize="lg"
        color="gray.800"
        textTransform="uppercase"
        mb={6}
        className="text-pacman"
      >
        Checkout
      </Heading>
      <VStack
        w={["100%", "50%"]}
        borderWidth="1px"
        p={4}
        className="border-class"
      >
        <Heading as="h2">Resumen</Heading>
        <p>Precio total: ${getTotal()}</p>
      </VStack>
      <Heading as="h2">Datos de Facturación</Heading>
      <form>
        <Input
          onChange={updateUser}
          placeholder="Nombre"
          name="name"
          type="text"
          mb={1}
          className="text-black p-2"
        />
        <Input
          onChange={updateUser}
          placeholder="Telefono"
          name="phone"
          type="number"
          mb={1}
          className="text-black p-2"
        />
        <Input
          onChange={updateUser}
          placeholder="Email"
          name="email"
          type="email"
          mb={1}
          className="text-black p-2"
        />
        <Input
          onChange={updateUser}
          placeholder="Repetir email"
          name="repeatedEmail"
          type="email"
          mb={1}
          className="text-black p-2"
        />
        {formErrors.repeatedEmail && (
          <Text color="red.500">{formErrors.repeatedEmail}</Text>
        )}
        {!emailMatch && (
          <Text color="red.500" fontSize="sm">
            Los campos de email no coinciden.
          </Text>
        )}
        <Button onClick={getOrder}>Comprar</Button>
      </form>
    </VStack>
  );
};
